import { FC, Fragment } from 'react'
import { PrismicRichText } from '@prismicio/react'
import { ImageField } from '@prismicio/client'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import tw from 'twin.macro'
import { Body, Grid, SmallHeader } from '../UI'
import { RichTextFields } from '../../types/prismic'
import config from '../../../tailwind.config'
import {
  richTextComponents,
  richTextStrongComponent,
} from '../../utils/richTextComponents'

type Props = {
  header: string
  subhead: RichTextFields
  items: Array<{
    icon: ImageField
    title: RichTextFields
    subtext: string
  }>
  show_header: boolean
  show_item_subtext: boolean
  background_color?: 'gb-blue' | 'gb-red' | 'gb-magenta' | 'gb-cream'
}

const AddressYourConcernsSection: FC<Props> = ({
  header,
  subhead,
  items,
  show_header,
  show_item_subtext,
  background_color,
}) => {
  const bgColor = config.theme.extend.colors[background_color]
  const whiteText = background_color !== 'gb-cream'

  return (
    <section
      css={[
        whiteText && tw`text-white`,
        { backgroundColor: bgColor },
        tw`pt-[40px] pb-[40px] lg:(pt-[60px] pb-[60px]) text-gb-blue font-domaine`,
      ]}
    >
      <Grid>
        {show_header && (
          <Fragment>
            {header && (
              <div tw="col-span-full lg:(col-span-5)">
                <h2
                  css={[
                    Body,
                    tw`mb-[15px] lg:mb-[25px]`,
                    whiteText && tw`text-white`,
                  ]}
                >
                  {header}
                </h2>
              </div>
            )}
            <div
              tw="col-span-full mb-8 lg:(col-start-1 col-span-full mb-5 pr-6)"
              css={whiteText && tw`text-white`}
            >
              <PrismicRichText
                field={subhead.richText}
                components={richTextComponents}
              />
            </div>
          </Fragment>
        )}
        <div
          css={[
            tw`col-span-full`,
            show_item_subtext
              ? tw`md:(grid grid-cols-2 gap-12) lg:grid-cols-3`
              : tw`grid grid-cols-3 gap-4 lg:gap-12`,
          ]}
        >
          {items.map((item, index) =>
            show_item_subtext ? (
              <div css={index > 0 && tw`pt-8 md:pt-0`}>
                <div
                  css={whiteText && tw`text-white`}
                  tw="flex items-center pb-1"
                >
                  {item.icon && (
                    <GatsbyImage
                      image={getImage(item.icon)}
                      alt={item.icon.alt}
                      tw="h-[50px] w-[50px] mr-2 min-w-[50px]"
                    />
                  )}
                  {item.title && (
                    <p
                      css={[SmallHeader, whiteText && tw`text-white font-bold`]}
                    >
                      <PrismicRichText
                        field={item.title.richText}
                        components={richTextStrongComponent}
                      />
                    </p>
                  )}
                </div>
                <p css={whiteText && tw`text-white`}>{item.subtext}</p>
              </div>
            ) : (
              <div key={index} tw="w-full flex justify-center">
                <div tw="flex flex-col items-center max-w-[300px]">
                  {item.icon && (
                    <GatsbyImage
                      image={getImage(item.icon)}
                      alt={item.icon.alt}
                      tw="h-[50px] w-[50px] min-w-[50px] lg:(h-[70px] w-[70px] min-w-[70px])"
                    />
                  )}
                  <p
                    css={whiteText && tw`text-white`}
                    tw="text-center text-[14px] md:text-[16px] lg:text-[18px]"
                  >
                    <PrismicRichText
                      field={item.title.richText}
                      components={richTextStrongComponent}
                    />
                  </p>
                </div>
              </div>
            )
          )}
        </div>
      </Grid>
    </section>
  )
}

export default AddressYourConcernsSection
